import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { DialogContent } from "@mui/material"

import i18n from "config/i18n"
import {
  withModalStep,
  withModalDebt,
  withModalPaymentSchedule,
  withModalSubscriptionEligibility,
} from "hocs/features"
import { useAppDispatch } from "redux/hooks"
import { showAlert, showNotice } from "redux/features/notice/noticeSlice"
import {
  closeDialog,
  updateDialogStep,
} from "redux/features/dialog/dialogSlice"
import PollingService from "redux/api/pollingService"
import {
  StepperComponent as Stepper,
  StepComponent as Step,
  StepContentComponent as StepContent,
} from "components/ui/Stepper"
import { Container, Headline } from "components/PaymentSchedule/elements"
import Back from "components/ui/ModalBack"
import Close from "components/ui/ModalClose"
import Notice from "components/ui/Notice"
import Title from "components/PaymentSchedule/Title"
import Download from "components/PaymentSchedule/Download"
import Invoices from "components/PaymentSchedule/Invoices"
import Actions from "components/PaymentSchedule/Actions"
import Providers from "components/PaymentSchedule/Sign/Providers"
import SmartId from "components/PaymentSchedule/Sign/SmartId"
import MobileId from "components/PaymentSchedule/Sign/MobileId"
import IdCard from "components/PaymentSchedule/Sign/IdCard"
import Nps from "components/Nps"
import NpsSuccess from "components/Nps/Success"
import {
  PS_SIGN_STEP_INITIAL,
  PS_SIGN_STEP_CHOOSE_PROVIDERS,
  PS_SIGN_STEP_SMART_ID,
  PS_SIGN_STEP_MOBILE_ID,
  PS_SIGN_STEP_ID_CARD,
  PS_SIGN_STEP_NPS,
  PS_SIGN_STEP_NPS_SUCCESS,
  PS_SIGN_STEP_FINAL,
} from "config"
import { DebtType, PaymentScheduleType } from "types"

type Props = {
  debt: DebtType
  payment_schedule: PaymentScheduleType
  needSubscription: boolean
  allowSubscribe: boolean
  step: number
}

const PaymentScheduleSignComponent = ({
  debt,
  payment_schedule,
  needSubscription,
  step = PS_SIGN_STEP_INITIAL,
}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { payment_schedule_invoices } = payment_schedule
  const isSigned = Boolean(payment_schedule?.signed_at)

  const isTitle = ![PS_SIGN_STEP_NPS, PS_SIGN_STEP_NPS_SUCCESS].includes(step)
  const isBackHidden =
    isSigned &&
    [
      PS_SIGN_STEP_INITIAL,
      PS_SIGN_STEP_NPS,
      PS_SIGN_STEP_NPS_SUCCESS,
      PS_SIGN_STEP_FINAL,
    ].includes(step)

  const handleClose = () => {
    if (PollingService.isPolling()) {
      return dispatch(
        showAlert({
          message: i18n.t("common.request_in_progress"),
        }),
      )
    }
    dispatch(closeDialog())
    navigate("/")
  }

  const handleChooseProvider = () => {
    dispatch(updateDialogStep({ step: PS_SIGN_STEP_CHOOSE_PROVIDERS }))
  }

  const handleSignSuccess = () => {
    dispatch(updateDialogStep({ step: PS_SIGN_STEP_FINAL }))

    setTimeout(() => {
      dispatch(updateDialogStep({ step: PS_SIGN_STEP_NPS }))
    }, 5000)

    dispatch(
      showNotice({
        message: t("notice.payment_schedule.sign.success"),
      }),
    )
  }

  const handleSignFailure = () => {
    dispatch(updateDialogStep({ step: PS_SIGN_STEP_CHOOSE_PROVIDERS }))

    dispatch(
      showAlert({
        message: t("notice.payment_schedule.sign.failure"),
      }),
    )
  }

  const handleNpsCreateSuccess = () => {
    dispatch(updateDialogStep({ step: PS_SIGN_STEP_NPS_SUCCESS }))
  }

  const handleNpsCreateFailure = () => {
    dispatch(updateDialogStep({ step: PS_SIGN_STEP_NPS }))
  }

  const handleViewPaymentSchedule = () => {
    navigate(`/debts/${debt.id}/payment_schedules/${payment_schedule.id}`)
  }

  const handleNewPaymentSchedule = () => {
    navigate(`/debts/${debt.id}/payment_schedules/new`)
  }

  const handleBack = () => {
    switch (step) {
      case PS_SIGN_STEP_INITIAL:
        if (!isSigned) {
          handleNewPaymentSchedule()
        }
        return
      case PS_SIGN_STEP_CHOOSE_PROVIDERS:
        dispatch(updateDialogStep({ step: PS_SIGN_STEP_INITIAL }))
        return
      case PS_SIGN_STEP_SMART_ID:
        dispatch(updateDialogStep({ step: PS_SIGN_STEP_CHOOSE_PROVIDERS }))
        return
      case PS_SIGN_STEP_MOBILE_ID:
        dispatch(updateDialogStep({ step: PS_SIGN_STEP_CHOOSE_PROVIDERS }))
        return
      case PS_SIGN_STEP_ID_CARD:
        dispatch(updateDialogStep({ step: PS_SIGN_STEP_CHOOSE_PROVIDERS }))
        return
      default:
        return
    }
  }

  const STEPS = [
    {
      label: PS_SIGN_STEP_INITIAL,
      component: (
        <>
          <Invoices invoices={payment_schedule_invoices} />
          <Actions
            action="sign"
            isSigned={isSigned}
            needSubscription={needSubscription}
            onSign={() => handleChooseProvider()}
            onDontLike={() => handleNewPaymentSchedule()}
          />
          <Notice>{t("features.payment_schedule.notice.sign")}</Notice>
        </>
      ),
    },
    {
      label: PS_SIGN_STEP_CHOOSE_PROVIDERS,
      component: (
        <Providers
          onChoose={(step) => dispatch(updateDialogStep({ step: step }))}
        />
      ),
    },
    {
      label: PS_SIGN_STEP_SMART_ID,
      component: (
        <SmartId
          paymentScheduleId={payment_schedule.id}
          onFailure={() => handleSignFailure()}
          onSuccess={() => handleSignSuccess()}
        />
      ),
    },
    {
      label: PS_SIGN_STEP_MOBILE_ID,
      component: (
        <MobileId
          paymentScheduleId={payment_schedule.id}
          onFailure={() => handleSignFailure()}
          onSuccess={() => handleSignSuccess()}
        />
      ),
    },
    {
      label: PS_SIGN_STEP_ID_CARD,
      component: (
        <IdCard
          paymentScheduleId={payment_schedule.id}
          onFailure={() => handleSignFailure()}
          onSuccess={() => handleSignSuccess()}
        />
      ),
    },
    {
      label: PS_SIGN_STEP_NPS,
      component: (
        <Nps
          paymentScheduleId={payment_schedule.id}
          onFailure={() => handleNpsCreateSuccess()}
          onSuccess={() => handleNpsCreateFailure()}
        />
      ),
    },
    {
      label: PS_SIGN_STEP_NPS_SUCCESS,
      component: <NpsSuccess onView={handleViewPaymentSchedule} />,
    },
    {
      label: PS_SIGN_STEP_FINAL,
      component: <Invoices invoices={payment_schedule_invoices} />,
    },
  ]

  return (
    <DialogContent>
      <Back
        disabled={needSubscription}
        hidden={isBackHidden}
        onClick={handleBack}
      />
      <Close onClick={handleClose} />
      <Container
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        {isTitle && (
          <Headline>
            <Title reference_number={debt?.reference_number}>
              {t("features.payment_schedule.title")}
            </Title>
            <Download debt={debt} paymentScheduleId={payment_schedule.id} />
          </Headline>
        )}
        <Stepper activeStep={step} orientation="vertical">
          {STEPS.map(({ component }, index) => (
            <Step key={index}>
              <StepContent>{component}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Container>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalStep,
  withModalDebt,
  withModalPaymentSchedule,
  withModalSubscriptionEligibility,
)(PaymentScheduleSignComponent)
